/** @jsxImportSource @emotion/react */
import { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import type { JSX as ReactJSX } from 'react/jsx-runtime';
import { MapContainer, TileLayer, Popup, Marker, useMap, useMapEvents, Circle } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { Site } from '../types/Site';
import { calculateDistance } from '../utils/distance';
import { submitFibreChangeRequest } from '../utils/siteUtils';
import { useAuth } from '../contexts/AuthContext';
import FilterControls from './FilterControls';
import { SitePopup } from './SitePopup';
import BottomNav from './BottomNav';
import styled from '@emotion/styled';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom marker icons for SPT sites
const createSvgIcon = (color: string, symbol: string) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 44" width="32" height="44">
    <path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 28 16 28s16-16 16-28C32 7.2 24.8 0 16 0z" fill="${color}" stroke="#fff" stroke-width="1.5"/>
    <circle cx="16" cy="15" r="10" fill="white" fill-opacity="0.3"/>
    <text x="16" y="20" text-anchor="middle" font-size="14" font-weight="bold" fill="white" font-family="Arial,sans-serif">${symbol}</text>
  </svg>`;
  return L.divIcon({
    html: svg,
    className: '',
    iconSize: [32, 44],
    iconAnchor: [16, 44],
    popupAnchor: [0, -44],
  });
};

const signalIcon = createSvgIcon('#d32f2f', 'S');
const pointsIcon = createSvgIcon('#1565c0', 'P');
const fibreIcon = createSvgIcon('#00bcd4', 'F');

const defaultIcon = new L.Icon.Default();

const getSiteIcon = (site: Site): L.Icon | L.DivIcon => {
  if (site.name.siteType === 'Signal' || site.name.siteType === 'Sub Signal') return signalIcon;
  if (site.name.siteType === 'Points') return pointsIcon;
  if (site.name.siteType === 'Fibre') return fibreIcon;
  return defaultIcon;
};

// Map controller component to handle map events and references
const MapController = ({ 
  siteToOpen, 
  onControllerReady,
  filteredSites 
}: { 
  siteToOpen: Site | null, 
  onControllerReady: (openPopup: (site: Site) => void) => void,
  filteredSites: Site[]
}) => {
  const map = useMap();
  const markerRefs = useRef<{ [key: string]: L.Marker }>({});
  const mapRef = useRef<L.Map | null>(null);
  const isMapReady = useRef(false);

  // Store map reference when component mounts and map is ready
  useEffect(() => {
    if (map && !isMapReady.current) {
      mapRef.current = map;
      isMapReady.current = true;
    }
  }, [map]);

  // Add page visibility handler
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && mapRef.current && isMapReady.current) {
        // When tab becomes visible, update the map view without reloading
        mapRef.current.invalidateSize();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Register the marker refs
  useEffect(() => {
    if (!isMapReady.current) return;

    const popupOpenForSite = (site: Site) => {
      // Close any existing popup
      map.closePopup();
      
      const key = `${site.coordinates.lat}-${site.coordinates.lng}`;
      const marker = markerRefs.current[key];
      
      if (marker) {
        // First pan to marker location
        map.panTo(marker.getLatLng());
        // Then open popup
        setTimeout(() => {
          marker.openPopup();
        }, 50);
      }
    };

    onControllerReady(popupOpenForSite);
  }, [onControllerReady, map, filteredSites]);

  // Open popup for selected site
  useEffect(() => {
    if (!isMapReady.current || !siteToOpen) return;

    // Close any existing popups
    map.closePopup();
    
    const key = `${siteToOpen.coordinates.lat}-${siteToOpen.coordinates.lng}`;
    const marker = markerRefs.current[key];
    if (marker) {
      // Immediately open popup and pan to it
      marker.openPopup();
      map.panTo(marker.getLatLng());
    }
  }, [siteToOpen, map]);

  // Prevent pinch zoom on mobile devices, yet allow map zooming with the map's controls
  useEffect(() => {
    if (!isMapReady.current) return;

    const mapContainer = map.getContainer();
    
    const preventTouchZoom = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        // Preventing default stops the pinch-to-zoom gesture
        e.preventDefault();
      }
    };
    
    // Add the touch event listeners
    mapContainer.addEventListener('touchstart', preventTouchZoom, { passive: false });
    mapContainer.addEventListener('touchmove', preventTouchZoom, { passive: false });
    
    return () => {
      // Clean up the event listeners on unmount
      mapContainer.removeEventListener('touchstart', preventTouchZoom);
      mapContainer.removeEventListener('touchmove', preventTouchZoom);
    };
  }, [map, isMapReady.current]);

  return null;
};

// Component that flies the map to a new center/zoom without remounting
const MapViewUpdater = ({ center, zoom }: { center: [number, number]; zoom: number }) => {
  const map = useMap();
  const initial = useRef(true);

  useEffect(() => {
    // Skip the first render (MapContainer already uses the initial center/zoom)
    if (initial.current) {
      initial.current = false;
      return;
    }
    map.setView(center, zoom);
  }, [center, zoom, map]);

  return null;
};

// Map layer configurations
const MAP_LAYERS = {
  standard: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    name: 'Standard Map'
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
    name: 'Satellite'
  },
  hybrid: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    overlayUrl: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png',
    attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community | Labels &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, <a href="https://carto.com/attributions">CARTO</a>',
    name: 'Hybrid'
  },
  terrain: {
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a>',
    name: 'Terrain'
  },
  dark: {
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    name: 'Dark Mode'
  }
};

const MapWrapper = styled.div`
  position: relative;
  height: 100vh;
  height: var(--app-height, 100vh);
  width: 100%;
  overflow: hidden;
  
  /* Ensure proper height on iOS */
  @supports (-webkit-touch-callout: none) {
    height: var(--app-height);
    
    /* Add safe area padding on the bottom */
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
  
  /* Fix Leaflet popup styling */
  .leaflet-popup-content-wrapper {
    padding: 0;
  }
  
  .leaflet-popup-content {
    margin: 0;
    width: auto !important;
  }
`;

const FilterWrapper = styled.div`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
`;

const NavigateButton = styled.button`
  padding: 8px 16px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 12px;
  width: 100%;
  
  &:hover {
    background-color: #0056b3;
  }
`;

const DistanceText = styled.p`
  font-size: 14px;
  color: #666;
  margin: 8px 0;
`;

const PopupContent = styled.div`
  /* Ensure proper popup display */
  width: 100%;
  min-width: 320px;
  max-width: 320px;
  display: block;
  overflow: visible;
`;

interface SearchLocation {
  lat: number;
  lon: number;
  zoom?: number;
  showAllSites?: boolean;
  selectedSiteId?: string;
}

interface SiteMapProps {
  sites: Site[];
  onLogout: () => void;
  userEmail?: string;
  onAccountSettings?: () => void;
  onAdminPanel?: () => void;
  onChangeAssetType?: () => void;
  isAdmin?: boolean;
  debug?: boolean;
  currentAssetType?: string;
}

// Long-press handler to add new fibre joints
const LongPressHandler = ({ onLongPress, enabled }: { onLongPress: (lat: number, lng: number) => void; enabled: boolean }) => {
  useMapEvents({
    contextmenu(e) {
      if (enabled) {
        e.originalEvent.preventDefault();
        onLongPress(e.latlng.lat, e.latlng.lng);
      }
    },
  });
  return null;
};

// Click handler for pin-drop mode (placing new fibre joint via button)
const PinDropClickHandler = ({ enabled, onDrop }: { enabled: boolean; onDrop: (lat: number, lng: number) => void }) => {
  useMapEvents({
    click(e) {
      if (enabled) {
        onDrop(e.latlng.lat, e.latlng.lng);
      }
    },
  });
  return null;
};

// Add a click handler to close popups when clicking on the map
const MapClickHandler = () => {
  const map = useMap();
  
  useEffect(() => {
    const handleMapClick = () => {
      map.closePopup();
    };
    
    map.on('click', handleMapClick);
    
    return () => {
      map.off('click', handleMapClick);
    };
  }, [map]);
  
  return null;
};

const SiteMap = ({ 
  sites, 
  onLogout, 
  userEmail, 
  onAccountSettings, 
  onAdminPanel, 
  onChangeAssetType,
  isAdmin = false, 
  debug = false,
  currentAssetType = ''
}: SiteMapProps): ReactJSX.Element => {
  const { user } = useAuth();
  // Add console log at the start of the component
  console.log('SiteMap received sites:', { count: sites.length, firstSite: sites[0] });
  
  const [searchLocation, setSearchLocation] = useState<SearchLocation | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>([54.5, -4]);
  const [mapZoom, setMapZoom] = useState(6);
  const [selectedSiteTypes, setSelectedSiteTypes] = useState<string[]>([]);
  const [elrFilter, setElrFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentLayer, setCurrentLayer] = useState('satellite');
  const [showRailwayOverlay, setShowRailwayOverlay] = useState(true);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [locating, setLocating] = useState(false);
  const [filterPosition, setFilterPosition] = useState({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const [siteToShow, setSiteToShow] = useState<Site | null>(null);
  const [openPopupFn, setOpenPopupFn] = useState<((site: Site) => void) | null>(null);
  const markerRefs = useRef<{ [key: string]: L.Marker }>({});
  const mapWrapperRef = useRef<HTMLDivElement>(null);
  
  // Add SDH-specific filters - only keep equipment filter
  const [equipmentFilter, setEquipmentFilter] = useState('');
  
  // New fibre joint state
  const [newJointPosition, setNewJointPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [showNewJointForm, setShowNewJointForm] = useState(false);
  const [pinDropMode, setPinDropMode] = useState(false);
  
  // Force admin for specific email
  const effectiveIsAdmin = userEmail === 'jamesperrin1999@gmail.com' ? true : isAdmin;
  
  // Prevent double-tap zoom on iOS
  useEffect(() => {
    const preventDoubleTapZoom = (e: TouchEvent) => {
      // Small delay to detect double tap
      const now = Date.now();
      const lastTouch = (e.target as any)._lastTouch || 0;
      (e.target as any)._lastTouch = now;
      
      if (now - lastTouch < 300) {
        // Double tap detected
        e.preventDefault();
      }
    };
    
    if (mapWrapperRef.current) {
      mapWrapperRef.current.addEventListener('touchend', preventDoubleTapZoom, { passive: false });
    }
    
    return () => {
      if (mapWrapperRef.current) {
        mapWrapperRef.current.removeEventListener('touchend', preventDoubleTapZoom);
      }
    };
  }, []);

  const hasActiveFilters = Boolean(
    selectedSiteTypes.length > 0 || 
    elrFilter || 
    searchLocation || 
    equipmentFilter
  );

  const handleLocateMe = useCallback(() => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc: [number, number] = [pos.coords.latitude, pos.coords.longitude];
        setUserLocation(loc);
        setMapCenter(loc);
        setMapZoom(15);
        setLocating(false);
      },
      (err) => {
        console.error('Geolocation error:', err);
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedSiteTypes([]);
    setElrFilter('');
    setSearchLocation(null);
    setSiteToShow(null);
    setMapCenter([54.5, -4]);
    setMapZoom(6);
    // Clear SDH filters too
    setEquipmentFilter('');
  }, []);

  // Handle long press on map to add new fibre joint
  const handleMapLongPress = useCallback((lat: number, lng: number) => {
    setNewJointPosition({ lat, lng });
    setShowNewJointForm(true);
    setPinDropMode(false);
  }, []);

  // Enter pin-drop mode from the bottom nav button
  const handleAddFibreJoint = useCallback(() => {
    setPinDropMode(true);
  }, []);

  // Handle pin drop click
  const handlePinDrop = useCallback((lat: number, lng: number) => {
    setNewJointPosition({ lat, lng });
    setShowNewJointForm(true);
    setPinDropMode(false);
  }, []);

  const handleSiteSelect = useCallback((site: Site) => {
    // First, clear any existing site selection
    if (openPopupFn) {
      // This will implicitly close existing popups
      setSiteToShow(site);
    }
  }, [openPopupFn]);

  const handleControllerReady = useCallback((openPopup: (site: Site) => void) => {
    setOpenPopupFn(() => openPopup);
  }, []);

  const filteredSites = useMemo(() => {
    // First apply filters
    const filtered = sites.filter(site => {
      // Skip sites without coordinates
      if (!site.coordinates || !site.coordinates.lat || !site.coordinates.lng) {
        return false;
      }

      // Apply location filter if set
      if (searchLocation) {
        const distance = calculateDistance(
          searchLocation.lat,
          searchLocation.lon,
          site.coordinates.lat,
          site.coordinates.lng
        );
        if (distance > 5) return false;
      }

      // Apply site type filter if selected
      if (selectedSiteTypes.length > 0 && !selectedSiteTypes.includes(site.name.siteType)) {
        return false;
      }

      // Apply ELR filter if entered
      if (elrFilter) {
        const siteElr = site.name.elrCode.split(',')[0].trim();
        if (!siteElr.includes(elrFilter)) {
          return false;
        }
      }

      // Apply equipment filter for SDH sites
      if (equipmentFilter && site.sdhInfo) {
        if (equipmentFilter.startsWith('SDH: ') && currentAssetType === 'telecoms_sites') {
          // For telecoms view, strip off the "SDH: " prefix
          const actualFilter = equipmentFilter.substring(5);
          if (site.sdhInfo.item !== actualFilter) {
            return false;
          }
        } else if (site.sdhInfo.item !== equipmentFilter) {
          return false;
        }
      }

      // Apply equipment filter for PDH sites
      if (equipmentFilter && site.pdhInfo) {
        if (equipmentFilter.startsWith('PDH: ') && currentAssetType === 'telecoms_sites') {
          // For telecoms view, strip off the "PDH: " prefix
          const actualFilter = equipmentFilter.substring(5);
          if (site.pdhInfo.equipment !== actualFilter) {
            return false;
          }
        } else if (site.pdhInfo.equipment !== equipmentFilter) {
          return false;
        }
      }

      // Apply equipment filter for LOC sites
      if (equipmentFilter && site.locInfo && site.locInfo.item !== equipmentFilter) {
        return false;
      }
      
      // Apply equipment filter for Telecoms sites
      if (equipmentFilter && site.telecomsInfo && 
          !equipmentFilter.startsWith('SDH: ') && 
          !equipmentFilter.startsWith('PDH: ') && 
          site.telecomsInfo.item !== equipmentFilter) {
        return false;
      }

      // Apply battery model filter
      if (equipmentFilter && site.batteryInfo) {
        // Filter for battery model
        if (!equipmentFilter.startsWith('strings:') && !equipmentFilter.startsWith('cells:')) {
          if (site.batteryInfo.batteryModel !== equipmentFilter) {
            return false;
          }
        }
        
        // Filter for cell strings count
        if (equipmentFilter.startsWith('strings:')) {
          const stringsCount = parseInt(equipmentFilter.substring(8), 10);
          if (isNaN(stringsCount) || site.batteryInfo.noCellStrings !== stringsCount) {
            return false;
          }
        }
        
        // Filter for cell count
        if (equipmentFilter.startsWith('cells:')) {
          const cellCount = parseInt(equipmentFilter.substring(6), 10);
          if (isNaN(cellCount) || site.batteryInfo.noCells !== cellCount) {
            return false;
          }
        }
      }

      return true;
    });

    return filtered;
  }, [sites, searchLocation, selectedSiteTypes, elrFilter, equipmentFilter, currentAssetType]);

  const openInMaps = useCallback((site: Site) => {
    const { lat, lng } = site.coordinates;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(url, '_blank');
  }, []);

  const handleSearch = useCallback((location: SearchLocation) => {
    setSearchLocation(location);
    setMapCenter([location.lat, location.lon]);
    
    // Set appropriate zoom level
    if (location.zoom) {
      setMapZoom(location.zoom);
    } else if (location.showAllSites) {
      setMapZoom(13); // Default city-level zoom
    } else if (location.selectedSiteId) {
      setMapZoom(18); // Default site-level zoom
    } else {
      setMapZoom(11); // Default search zoom
    }
  }, []);

  const toggleFilters = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  const saveMarkerRef = useCallback((marker: L.Marker, site: Site) => {
    if (marker) {
      const key = `${site.coordinates.lat}-${site.coordinates.lng}`;
      markerRefs.current[key] = marker;
      
      // If this is the site we want to show, open its popup immediately
      if (siteToShow && 
          siteToShow.coordinates.lat === site.coordinates.lat && 
          siteToShow.coordinates.lng === site.coordinates.lng) {
        setTimeout(() => {
          marker.openPopup();
        }, 100);
      }
    }
  }, [siteToShow]);

  // Effect to find and click a marker when siteToShow changes
  useEffect(() => {
    if (siteToShow && openPopupFn) {
      // Directly use the openPopupFn
      openPopupFn(siteToShow);
      
      // Pan to the marker
      setMapCenter([siteToShow.coordinates.lat, siteToShow.coordinates.lng]);
      setMapZoom(17); // Zoom in enough to see the site clearly
      
      // Reset state after handling
      setTimeout(() => {
        setSiteToShow(null);
      }, 100);
    }
  }, [siteToShow, openPopupFn]);

  return (
    <MapWrapper ref={mapWrapperRef}>
      {showFilters && (
        <FilterControls
          selectedSiteTypes={selectedSiteTypes}
          onSiteTypesChange={setSelectedSiteTypes}
          elrFilter={elrFilter}
          onElrFilterChange={setElrFilter}
          initialPosition={filterPosition}
          onClose={() => setShowFilters(false)}
          sites={sites}
          equipmentFilter={equipmentFilter}
          onEquipmentFilterChange={setEquipmentFilter}
          currentAssetType={currentAssetType}
        />
      )}
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ height: '100%', width: '100%', cursor: pinDropMode ? 'crosshair' : '' }}
      >
        <MapViewUpdater center={mapCenter} zoom={mapZoom} />
        <TileLayer
          key={currentLayer}
          url={MAP_LAYERS[currentLayer as keyof typeof MAP_LAYERS].url}
          attribution={MAP_LAYERS[currentLayer as keyof typeof MAP_LAYERS].attribution}
        />
        {currentLayer === 'hybrid' && (
          <TileLayer 
            url={MAP_LAYERS.hybrid.overlayUrl}
            zIndex={10}
            pane="overlayPane"
          />
        )}
        {showRailwayOverlay && (
          <TileLayer
            url="https://tiles.openrailwaymap.org/standard/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openrailwaymap.org/">OpenRailwayMap</a> contributors'
            zIndex={20}
            pane="overlayPane"
            opacity={0.7}
          />
        )}
        <MapController 
          siteToOpen={siteToShow} 
          onControllerReady={handleControllerReady}
          filteredSites={filteredSites}
        />
        <MapClickHandler />
        <LongPressHandler onLongPress={handleMapLongPress} enabled={currentAssetType === 'fibre_sites'} />
        <PinDropClickHandler enabled={pinDropMode} onDrop={handlePinDrop} />
        <MarkerClusterGroup
          chunkedLoading
          maxClusterRadius={50}
          spiderfyOnMaxZoom={true}
          polygonOptions={{
            fillColor: '#1978c8',
            color: '#1978c8',
            weight: 2,
            opacity: 1,
            fillOpacity: 0.2
          }}
        >
          {filteredSites.map((site, index) => {
            // Create a component to access the map context
            const MarkerWithPopup = () => {
              const map = useMap();
              
              return (
                <Marker
                  position={[site.coordinates.lat, site.coordinates.lng]}
                  icon={getSiteIcon(site)}
                  eventHandlers={{
                    add: (e) => saveMarkerRef(e.target, site),
                    click: (e) => {
                      // Close all other popups first
                      map.closePopup();
                      // Then open this popup
                      e.target.openPopup();
                    }
                  }}
                >
                  <Popup 
                    closeOnClick={true} 
                    closeButton={true} 
                    autoClose={true}
                    autoPan={true}
                    maxWidth={320}
                    minWidth={320}
                  >
                    <PopupContent onClick={(e) => e.stopPropagation()}>
                      <SitePopup site={site} />
                    </PopupContent>
                  </Popup>
                </Marker>
              );
            };
            
            // Use coordinates as the key to ensure uniqueness
            return <MarkerWithPopup key={`${site.coordinates.lat}-${site.coordinates.lng}`} />;
          })}
        </MarkerClusterGroup>
        {userLocation && (
          <>
            <Circle
              center={userLocation}
              radius={12}
              pathOptions={{ color: '#4285F4', fillColor: '#4285F4', fillOpacity: 1, weight: 3, opacity: 1 }}
            />
            <Circle
              center={userLocation}
              radius={100}
              pathOptions={{ color: '#4285F4', fillColor: '#4285F4', fillOpacity: 0.15, weight: 1 }}
            />
          </>
        )}
      </MapContainer>
      <BottomNav
        onSearch={handleSearch}
        onLogout={onLogout}
        onLayerChange={setCurrentLayer}
        currentLayer={currentLayer}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        onFilterClick={toggleFilters}
        onSiteSelect={handleSiteSelect}
        onAccountSettings={onAccountSettings}
        onAdminPanel={onAdminPanel}
        onChangeAssetType={onChangeAssetType}
        userEmail={userEmail}
        sites={sites}
        isAdmin={effectiveIsAdmin}
        showRailwayOverlay={showRailwayOverlay}
        onToggleRailwayOverlay={() => setShowRailwayOverlay(prev => !prev)}
        onLocateMe={handleLocateMe}
        locating={locating}
        onAddFibreJoint={currentAssetType === 'fibre_sites' ? handleAddFibreJoint : undefined}
      />
      {pinDropMode && (
        <PinDropBanner>
          <PinDropBannerIcon>📍</PinDropBannerIcon>
          <PinDropBannerText>Tap the map to place a new fibre joint</PinDropBannerText>
          <PinDropCancelButton onClick={() => setPinDropMode(false)}>Cancel</PinDropCancelButton>
        </PinDropBanner>
      )}
      {showNewJointForm && newJointPosition && (
        <NewJointFormOverlay
          lat={newJointPosition.lat}
          lng={newJointPosition.lng}
          userEmail={user?.email || ''}
          onClose={() => { setShowNewJointForm(false); setNewJointPosition(null); }}
        />
      )}
    </MapWrapper>
  );
};

// ==========================================
// New Fibre Joint Form Overlay Component
// ==========================================
const NewJointFormOverlay = ({ lat, lng, userEmail, onClose }: {
  lat: number;
  lng: number;
  userEmail: string;
  onClose: () => void;
}) => {
  const [formData, setFormData] = useState({
    joint_name: '',
    cable_name: '',
    section_number: '',
    elr: '',
    location: '',
    access: '',
    lat: lat.toFixed(6),
    lng: lng.toFixed(6),
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const handleSubmit = async () => {
    if (!formData.cable_name.trim()) {
      setSubmitStatus({ message: 'Cable Name is required', type: 'error' });
      return;
    }
    setSubmitting(true);
    setSubmitStatus(null);
    try {
      await submitFibreChangeRequest({
        fibre_site_id: null as any,
        request_type: 'new',
        original_joint_name: '',
        original_cable_name: '',
        user_email: userEmail,
        suggested_joint_name: formData.joint_name || undefined,
        suggested_cable_name: formData.cable_name || undefined,
        suggested_section_number: formData.section_number || undefined,
        suggested_elr: formData.elr || undefined,
        suggested_location: formData.location || undefined,
        suggested_access: formData.access || undefined,
        suggested_lat: parseFloat(formData.lat) || undefined,
        suggested_lng: parseFloat(formData.lng) || undefined,
        notes: formData.notes || undefined,
        status: 'pending',
      });
      setSubmitStatus({ message: 'New joint request submitted! An admin will review it shortly.', type: 'success' });
    } catch (err) {
      setSubmitStatus({ message: err instanceof Error ? err.message : 'Failed to submit request', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <NewJointOverlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <NewJointCard>
        <NewJointHeader>
          <NewJointTitle>Add New Fibre Joint</NewJointTitle>
          <NewJointCloseBtn onClick={onClose}>&times;</NewJointCloseBtn>
        </NewJointHeader>

        {submitStatus?.type === 'success' ? (
          <NewJointBody>
            <NewJointSuccess>{submitStatus.message}</NewJointSuccess>
            <NewJointActions>
              <NewJointSubmitBtn onClick={onClose}>Done</NewJointSubmitBtn>
            </NewJointActions>
          </NewJointBody>
        ) : (
          <NewJointBody>
            {submitStatus?.type === 'error' && (
              <NewJointError>{submitStatus.message}</NewJointError>
            )}

            <NewJointGrid>
              <NewJointField>
                <NewJointLabel>Joint Name</NewJointLabel>
                <NewJointInput type="text" placeholder="e.g. F-DOCC-YRKC-A-01"
                  value={formData.joint_name}
                  onChange={(e) => setFormData(d => ({ ...d, joint_name: e.target.value }))}
                />
              </NewJointField>
              <NewJointField>
                <NewJointLabel>Cable Name *</NewJointLabel>
                <NewJointInput type="text" placeholder="e.g. F-DOCC-YRKC-A"
                  value={formData.cable_name}
                  onChange={(e) => setFormData(d => ({ ...d, cable_name: e.target.value }))}
                />
              </NewJointField>
              <NewJointField>
                <NewJointLabel>Section Number</NewJointLabel>
                <NewJointInput type="text" placeholder="e.g. DOCC-YRKC-A-01"
                  value={formData.section_number}
                  onChange={(e) => setFormData(d => ({ ...d, section_number: e.target.value }))}
                />
              </NewJointField>
              <NewJointField>
                <NewJointLabel>ELR</NewJointLabel>
                <NewJointInput type="text" placeholder="ELR reference"
                  value={formData.elr}
                  onChange={(e) => setFormData(d => ({ ...d, elr: e.target.value }))}
                />
              </NewJointField>
              <NewJointField>
                <NewJointLabel>Location</NewJointLabel>
                <NewJointInput type="text" placeholder="e.g. Up Main"
                  value={formData.location}
                  onChange={(e) => setFormData(d => ({ ...d, location: e.target.value }))}
                />
              </NewJointField>
              <NewJointField>
                <NewJointLabel>Access</NewJointLabel>
                <NewJointInput type="text" placeholder="Access info"
                  value={formData.access}
                  onChange={(e) => setFormData(d => ({ ...d, access: e.target.value }))}
                />
              </NewJointField>
              <NewJointField>
                <NewJointLabel>Latitude</NewJointLabel>
                <NewJointInput type="text"
                  value={formData.lat}
                  onChange={(e) => setFormData(d => ({ ...d, lat: e.target.value }))}
                />
              </NewJointField>
              <NewJointField>
                <NewJointLabel>Longitude</NewJointLabel>
                <NewJointInput type="text"
                  value={formData.lng}
                  onChange={(e) => setFormData(d => ({ ...d, lng: e.target.value }))}
                />
              </NewJointField>
            </NewJointGrid>

            <NewJointField style={{ marginTop: '8px' }}>
              <NewJointLabel>Notes (optional)</NewJointLabel>
              <NewJointTextarea
                rows={2}
                placeholder="Any additional info..."
                value={formData.notes}
                onChange={(e) => setFormData(d => ({ ...d, notes: e.target.value }))}
              />
            </NewJointField>

            <NewJointActions>
              <NewJointCancelBtn onClick={onClose}>Cancel</NewJointCancelBtn>
              <NewJointSubmitBtn onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit for Review'}
              </NewJointSubmitBtn>
            </NewJointActions>
          </NewJointBody>
        )}
      </NewJointCard>
    </NewJointOverlay>
  );
};

// Styled components for Pin Drop Banner
const PinDropBanner = styled.div`
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(0, 188, 212, 0.95);
  backdrop-filter: blur(10px);
  padding: 12px 20px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  animation: slideDown 0.3s ease-out;

  @keyframes slideDown {
    from { transform: translateX(-50%) translateY(-20px); opacity: 0; }
    to { transform: translateX(-50%) translateY(0); opacity: 1; }
  }
`;

const PinDropBannerIcon = styled.span`
  font-size: 24px;
  animation: bounce 1s ease infinite;

  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
  }
`;

const PinDropBannerText = styled.span`
  color: white;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
`;

const PinDropCancelButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: white;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

// Styled components for New Joint Form
const NewJointOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 16px;
`;

const NewJointCard = styled.div`
  background: #1a1a1a;
  border-radius: 12px;
  width: 100%;
  max-width: 440px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(0, 188, 212, 0.3);
`;

const NewJointHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 18px;
  background: linear-gradient(135deg, #00bcd4, #0097a7);
  border-radius: 12px 12px 0 0;
`;

const NewJointTitle = styled.h3`
  margin: 0;
  color: white;
  font-size: 16px;
  font-weight: 600;
`;

const NewJointCloseBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  font-size: 22px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  &:hover { color: white; }
`;

const NewJointBody = styled.div`
  padding: 16px 18px;
`;

const NewJointGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const NewJointField = styled.div``;

const NewJointLabel = styled.label`
  display: block;
  font-size: 11px;
  color: #999;
  margin-bottom: 3px;
  font-weight: 500;
`;

const NewJointInput = styled.input`
  width: 100%;
  padding: 7px 8px;
  background: #2a2a2a;
  border: 1px solid #444;
  border-radius: 6px;
  color: #fff;
  font-size: 13px;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #00bcd4; }
  &::placeholder { color: #666; font-size: 11px; }
`;

const NewJointTextarea = styled.textarea`
  width: 100%;
  padding: 7px 8px;
  background: #2a2a2a;
  border: 1px solid #444;
  border-radius: 6px;
  color: #fff;
  font-size: 13px;
  box-sizing: border-box;
  resize: vertical;
  &:focus { outline: none; border-color: #00bcd4; }
  &::placeholder { color: #666; font-size: 11px; }
`;

const NewJointActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 14px;
`;

const NewJointCancelBtn = styled.button`
  padding: 8px 18px;
  background: #333;
  border: none;
  border-radius: 6px;
  color: #ccc;
  cursor: pointer;
  font-size: 13px;
  &:hover { background: #444; }
`;

const NewJointSubmitBtn = styled.button`
  padding: 8px 18px;
  background: #00838f;
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  &:hover { background: #00acc1; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const NewJointSuccess = styled.div`
  color: #4ade80;
  font-size: 14px;
  padding: 12px 0;
  text-align: center;
`;

const NewJointError = styled.div`
  color: #f87171;
  font-size: 13px;
  margin-bottom: 10px;
  padding: 8px;
  background: rgba(248, 113, 113, 0.1);
  border-radius: 6px;
`;

export default SiteMap; 